import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { type SWRConfiguration } from 'swr';
import { ForbiddenError, NotFoundError } from '@shared/api/errors/errors';
import { AdminCommandDialog } from '@features/withdrawal';
import { useMerchantSettings, type MerchantSettingsPane } from '@features/merchant-settings';
import { useApiServices } from '@shared/lib/use-api-services';
import { formatDateTime } from '@shared/lib/format-date-time';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { cn } from '@shared/lib/utils';
import type { AdminCommandDto, AutoWithdrawalRuleDto, WithdrawalLimitProfileDto, WithdrawalLimitScope } from '@shared/api/contracts';
import type { WithdrawalAddressItem } from '@shared/api/contracts/security';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusBanner,
  Tabs,
  TabsList,
  TabsTrigger,
  useToast,
} from '@shared/ui';

const SCOPES: WithdrawalLimitScope[] = ['ALL', 'ASSET', 'NETWORK', 'ASSET_NETWORK'];
const NETWORKS = ['TRC20', 'ERC20', 'BEP20'] as const;
const swrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  shouldRetryOnError: (error) => !(error instanceof ForbiddenError) && !(error instanceof NotFoundError),
  errorRetryCount: 0,
};

function Field({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={cn('w-full space-y-1.5', className)}>
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Badge({ tone, children }: { tone: 'ok' | 'wait' | 'off' | 'fail'; children: ReactNode }) {
  const toneClass = {
    ok: 'bg-success text-success-foreground',
    wait: 'bg-pending text-pending-foreground',
    off: 'bg-muted text-muted-foreground',
    fail: 'bg-fail text-fail-foreground',
  }[tone];
  return <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', toneClass)}>{children}</span>;
}

export function MerchantSettingsDialog() {
  const { t } = useTranslation('cabinet');
  const { request, closeMerchantSettings, openMerchantPicker } = useMerchantSettings();
  const merchant = request && request !== 'picker' ? request : null;
  const [pane, setPane] = useState<MerchantSettingsPane>('limits');

  useEffect(() => {
    if (merchant) setPane(merchant.pane);
  }, [merchant]);

  return (
    <Dialog open={request !== null} onOpenChange={(open) => !open && closeMerchantSettings()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{merchant?.email || t('withdrawalControl.merchant.title')}</DialogTitle>
          <DialogDescription>
            {request === 'picker' ? t('withdrawalControl.merchant.pick') : t('withdrawalControl.merchant.opened', { id: merchant?.merchantId ?? '' })}
          </DialogDescription>
        </DialogHeader>
        {merchant ? (
          <MerchantBody
            merchantId={merchant.merchantId}
            pane={pane}
            highlight={merchant.highlight}
            fromPicker={merchant.fromPicker}
            onPane={setPane}
            onBack={openMerchantPicker}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function MerchantBody({
  merchantId,
  pane,
  highlight,
  fromPicker,
  onPane,
  onBack,
}: {
  merchantId: string;
  pane: MerchantSettingsPane;
  highlight: string;
  fromPicker: boolean;
  onPane: (pane: MerchantSettingsPane) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const services = useApiServices();
  const limits = useSWR(['merchant-limits', merchantId], () => services.withdrawals.getMerchantLimits(merchantId), swrConfig);
  const addresses = useSWR(['merchant-addresses', merchantId], () => services.withdrawals.getMerchantWithdrawalAddresses(merchantId), swrConfig);
  const balance = useSWR(['merchant-balance', merchantId], () => services.withdrawals.getMerchantAutoWithdrawal(merchantId), swrConfig);
  const errors = [limits.error, addresses.error, balance.error];
  const missing = errors.some((error) => error instanceof NotFoundError);
  const failed = errors.find((error) => error && !(error instanceof NotFoundError));
  const loading = !missing && !failed && (limits.isLoading || addresses.isLoading || balance.isLoading) && !limits.data && !addresses.data && !balance.data;

  if (missing) return <StatusBanner variant="destructive">{t('withdrawalControl.merchant.notFound')}</StatusBanner>;
  if (failed) {
    return <StatusBanner variant="destructive">{getApiErrorMessage(failed, t, 'withdrawalControl.merchant.loadError')}</StatusBanner>;
  }
  if (loading) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="space-y-4">
      {fromPicker ? (
        <Button type="button" size="sm" variant="outline" onClick={onBack}>{t('withdrawalControl.merchant.back')}</Button>
      ) : null}
      <Tabs value={pane} onValueChange={(value) => onPane(value as MerchantSettingsPane)}>
        <TabsList className="h-auto w-full flex-wrap justify-start">
          <TabsTrigger value="limits">{t('withdrawalControl.limits.title')}</TabsTrigger>
          <TabsTrigger value="addresses">{t('withdrawalControl.addresses.title')}</TabsTrigger>
          <TabsTrigger value="auto">{t('withdrawalControl.auto.title')}</TabsTrigger>
        </TabsList>
      </Tabs>
      {pane === 'limits' ? <LimitEditor merchantId={merchantId} profiles={limits.data?.profiles ?? []} onSaved={() => void limits.mutate()} /> : null}
      {pane === 'addresses' ? (
        <AddressEditor
          items={addresses.data?.items ?? []}
          onAdd={async (payload) => {
            await services.withdrawals.addMerchantWithdrawalAddress(merchantId, payload);
            await addresses.mutate();
          }}
          onDelete={async (entryId, command) => {
            await services.withdrawals.deleteMerchantWithdrawalAddress(merchantId, entryId, {
              reason: command.reason,
              secondFactor: command.secondFactor,
            });
            await addresses.mutate();
          }}
        />
      ) : null}
      {pane === 'auto' ? (
        <AutoRules
          highlight={highlight}
          rules={balance.data?.auto_withdraw?.rules ?? []}
          onVerify={async (rule, command) => {
            try {
              await services.withdrawals.verifyAutoRule(merchantId, String(rule.id), {
                expectedAddress: rule.destinationAddress,
                reason: command.reason,
                secondFactor: command.secondFactor,
              });
              toast(t('withdrawalControl.auto.verified'), 'success');
              await balance.mutate();
            } catch (error) {
              toast(getApiErrorMessage(error, t, 'withdrawalControl.auto.error'), 'error');
              await balance.mutate();
              throw error;
            }
          }}
        />
      ) : null}
    </div>
  );
}

function LimitEditor({ merchantId, profiles, onSaved }: { merchantId: string; profiles: WithdrawalLimitProfileDto[]; onSaved: () => void }) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const services = useApiServices();
  const [scope, setScope] = useState<WithdrawalLimitScope>('ALL');
  const [assetCode, setAssetCode] = useState('USDT');
  const [networkCode, setNetworkCode] = useState('TRC20');
  const [single, setSingle] = useState('1000');
  const [daily, setDaily] = useState('1000');
  const [monthly, setMonthly] = useState('1000');
  const [open, setOpen] = useState(false);
  const current = useMemo(
    () => profiles.find((item) => item.scope === scope && (item.assetCode ?? '') === (scope.includes('ASSET') ? assetCode : '') && (item.networkCode ?? '') === (scope.includes('NETWORK') ? networkCode : '')),
    [profiles, scope, assetCode, networkCode],
  );

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('withdrawalControl.fields.scope')}>
          <Select value={scope} onValueChange={(value) => setScope(value as WithdrawalLimitScope)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {SCOPES.map((item) => <SelectItem key={item} value={item}>{t(`withdrawals.limits.scope.${item}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        {scope.includes('ASSET') ? (
          <Field label={t('withdrawalControl.fields.asset')}>
            <Input value={assetCode} onChange={(event) => setAssetCode(event.target.value)} placeholder="USDT" />
          </Field>
        ) : null}
        {scope.includes('NETWORK') ? (
          <Field label={t('withdrawalControl.fields.network')}>
            <Select value={networkCode} onValueChange={setNetworkCode}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {NETWORKS.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        ) : null}
        <Field label={t('withdrawalControl.fields.single')}>
          <Input value={single} inputMode="decimal" onChange={(event) => setSingle(event.target.value)} />
        </Field>
        <Field label={t('withdrawalControl.fields.daily')}>
          <Input value={daily} inputMode="decimal" onChange={(event) => setDaily(event.target.value)} />
        </Field>
        <Field label={t('withdrawalControl.fields.monthly')}>
          <Input value={monthly} inputMode="decimal" onChange={(event) => setMonthly(event.target.value)} />
        </Field>
      </div>
      <p className="text-xs text-muted-foreground">{t('withdrawalControl.limits.pendingNote')}</p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={() => setOpen(true)}>{t('withdrawalControl.limits.save')}</Button>
        {scope === 'ALL' ? null : (
          <Button type="button" size="sm" variant="outline" onClick={() => { setSingle(''); setOpen(true); }}>
            {t('withdrawalControl.limits.remove')}
          </Button>
        )}
      </div>
      <AdminCommandDialog
        open={open}
        title={t('withdrawalControl.limits.save')}
        onOpenChange={setOpen}
        onConfirm={async (command) => {
          const deleting = !single && scope !== 'ALL';
          try {
            await services.withdrawals.proposeLimitChange(merchantId, {
              scope,
              assetCode: scope.includes('ASSET') ? assetCode : undefined,
              networkCode: scope.includes('NETWORK') ? networkCode : undefined,
              action: deleting ? 'DELETE' : 'UPSERT',
              version: current?.version ?? 0,
              singleUsdt: deleting ? undefined : single,
              dailyUsdt: deleting ? undefined : daily,
              monthlyUsdt: deleting ? undefined : monthly,
              secondFactor: command.secondFactor,
            });
            setOpen(false);
            toast(t('withdrawalControl.limits.saved'), 'success');
            onSaved();
          } catch (error) {
            toast(getApiErrorMessage(error, t, 'withdrawalControl.limits.error'), 'error');
          }
        }}
      />
    </div>
  );
}

function AddressStatus({ item }: { item: WithdrawalAddressItem }) {
  const { t, i18n } = useTranslation('cabinet');
  if (item.amlBlocked) return <Badge tone="fail">{t('withdrawalControl.addresses.statusBlocked')}</Badge>;
  if (item.status === 'pending') {
    return <Badge tone="wait">{t('withdrawalControl.addresses.statusPending', { datetime: formatDateTime(item.activeFrom ?? '', i18n.language) })}</Badge>;
  }
  return <Badge tone="ok">{t('withdrawalControl.addresses.statusReady')}</Badge>;
}

function AddressEditor({
  items,
  onAdd,
  onDelete,
}: {
  items: WithdrawalAddressItem[];
  onAdd: (payload: { network: string; address: string; label?: string; reason: string; secondFactor: AdminCommandDto['secondFactor'] }) => Promise<void>;
  onDelete: (entryId: string, command: AdminCommandDto) => Promise<void>;
}) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const [network, setNetwork] = useState<(typeof NETWORKS)[number]>('TRC20');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState<'add' | 'delete' | null>(null);
  const [entryId, setEntryId] = useState('');

  return (
    <div className="space-y-3">
      {items.length ? (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id} className="flex flex-col gap-3 rounded-xl border border-border bg-background p-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium">{item.network}{item.label ? ` · ${item.label}` : ''}</p>
                <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{item.address}</p>
                <div className="mt-2"><AddressStatus item={item} /></div>
              </div>
              <Button type="button" size="sm" variant="outline" className="shrink-0 self-start" onClick={() => { setEntryId(String(item.id)); setMode('delete'); }}>
                {t('withdrawalControl.addresses.remove')}
              </Button>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-muted-foreground">{t('withdrawalControl.addresses.empty')}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('withdrawalControl.fields.network')}>
          <Select value={network} onValueChange={(value) => setNetwork(value as (typeof NETWORKS)[number])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {NETWORKS.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t('withdrawalControl.addresses.label')}>
          <Input value={label} onChange={(event) => setLabel(event.target.value)} />
        </Field>
        <Field label={t('withdrawalControl.addresses.address')} className="sm:col-span-2">
          <Input value={address} className="font-mono text-[13px]" onChange={(event) => setAddress(event.target.value)} />
        </Field>
      </div>
      <Button type="button" size="sm" onClick={() => setMode('add')}>{t('withdrawalControl.addresses.add')}</Button>
      <AdminCommandDialog
        open={mode !== null}
        title={mode === 'delete' ? t('withdrawalControl.addresses.remove') : t('withdrawalControl.addresses.add')}
        onOpenChange={(open) => !open && setMode(null)}
        onConfirm={async (command) => {
          try {
            if (mode === 'add') {
              await onAdd({ network, address: address.trim(), label: label.trim() || undefined, reason: command.reason, secondFactor: command.secondFactor });
            } else {
              await onDelete(entryId, command);
            }
            setMode(null);
          } catch (error) {
            toast(getApiErrorMessage(error, t, 'withdrawalControl.addresses.error'), 'error');
          }
        }}
      />
    </div>
  );
}

function AutoRules({
  rules,
  highlight,
  onVerify,
}: {
  rules: AutoWithdrawalRuleDto[];
  highlight: string;
  onVerify: (rule: AutoWithdrawalRuleDto, command: AdminCommandDto) => Promise<void>;
}) {
  const { t } = useTranslation('cabinet');
  const [rule, setRule] = useState<AutoWithdrawalRuleDto | null>(null);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t('withdrawalControl.auto.hint')}</p>
      {rules.length ? (
        <ul className="space-y-2">
          {rules.map((item) => {
            const marked = highlight === `${item.assetCode}:${item.networkCode}`;
            return (
              <li key={String(item.id)} className={cn('rounded-xl border border-border bg-background p-3', marked && 'border-pending-foreground bg-pending/10')}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{item.assetCode} · {item.networkCode}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <Badge tone={item.enabled ? 'ok' : 'off'}>
                        {item.enabled ? t('withdrawalControl.auto.statusOn') : t('withdrawalControl.auto.statusOff')}
                      </Badge>
                      <Badge tone={item.hasVerification ? 'ok' : 'wait'}>
                        {item.hasVerification ? t('withdrawalControl.auto.statusConfirmed') : t('withdrawalControl.auto.statusWaiting')}
                      </Badge>
                    </div>
                    <p className="mt-2 break-all font-mono text-xs text-muted-foreground">{item.destinationAddress}</p>
                  </div>
                  {item.hasVerification ? null : (
                    <Button type="button" size="sm" className="shrink-0 self-start" onClick={() => setRule(item)}>
                      {t('withdrawalControl.auto.verify')}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      ) : <p className="text-sm text-muted-foreground">{t('withdrawalControl.auto.empty')}</p>}
      <AdminCommandDialog
        open={Boolean(rule)}
        title={t('withdrawalControl.auto.verify')}
        description={rule ? `${rule.assetCode} · ${rule.networkCode}` : undefined}
        onOpenChange={(open) => !open && setRule(null)}
        onConfirm={async (command) => {
          if (!rule) return;
          try {
            await onVerify(rule, command);
            setRule(null);
          } catch {
            return;
          }
        }}
      />
    </div>
  );
}
